<template>
  <section id="experience" class="py-20 bg-white dark:bg-gray-800">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="section-title" :class="{ 'khmer': isKhmer }">
        {{ $t('experience.title') }}
      </h2>

      <div class="relative">
        <!-- Timeline Line -->
        <div class="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary-200 dark:bg-primary-800 transform md:-translate-x-0.5"></div>

        <!-- Experience Items -->
        <div class="space-y-8">
          <div
            v-for="(experience, index) in experiences"
            :key="experience.id"
            :class="[
              'relative flex items-center',
              index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
            ]"
          >
            <!-- Timeline Dot -->
            <div class="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-600 rounded-full transform -translate-x-1.5 md:-translate-x-1.5 z-10"></div>

            <!-- Content Card -->
            <div :class="[
              'ml-12 md:ml-0 w-full',
              index % 2 === 0 ? 'md:pr-8 md:w-1/2' : 'md:pl-8 md:w-1/2 md:ml-auto'
            ]">
              <div class="card p-6 hover:shadow-xl transition-all duration-300">
                <!-- Duration Badge -->
                <div class="inline-flex items-center px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm rounded-full mb-4">
                  <CalendarIcon class="w-4 h-4 mr-2" />
                  {{ experience.startDate }} - {{ experience.endDate || $t('experience.present') }}
                </div>

                <!-- Job Title & Company -->
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {{ experience.position }}
                </h3>
                <div class="flex items-center mb-4">
                  <BuildingOfficeIcon class="w-4 h-4 text-gray-500 mr-2" />
                  <span class="text-primary-600 dark:text-primary-400 font-medium">
                    {{ experience.company }}
                  </span>
                  <span class="text-gray-500 dark:text-gray-400 mx-2">•</span>
                  <span class="text-gray-600 dark:text-gray-300">
                    {{ experience.location }}
                  </span>
                </div>

                <!-- Description -->
                <p class="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                  {{ experience.description }}
                </p>

                <!-- Key Achievements -->
                <div class="mb-4">
                  <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Key Achievements:
                  </h4>
                  <ul class="space-y-1">
                    <li
                      v-for="achievement in experience.achievements"
                      :key="achievement"
                      class="flex items-start text-sm text-gray-600 dark:text-gray-300"
                    >
                      <CheckIcon class="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      {{ achievement }}
                    </li>
                  </ul>
                </div>

                <!-- Technologies Used -->
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="tech in experience.technologies"
                    :key="tech"
                    class="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full"
                  >
                    {{ tech }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import {
  CalendarIcon,
  BuildingOfficeIcon,
  CheckIcon
} from '@heroicons/vue/24/outline'

const store = useStore()
const isKhmer = computed(() => store.getters.isKhmer)

const experiences = [
  {
    id: 1,
    position: 'Senior Full Stack Developer',
    company: 'TechCorp Solutions',
    location: 'Phnom Penh, Cambodia',
    startDate: 'Jan 2022',
    endDate: null, // Current job
    description: 'Leading development of enterprise web applications using modern JavaScript frameworks. Managing a team of 5 developers and overseeing technical architecture decisions.',
    achievements: [
      'Improved application performance by 40% through code optimization',
      'Led successful migration from legacy PHP system to modern Vue.js/Node.js stack',
      'Implemented CI/CD pipeline reducing deployment time by 60%',
      'Mentored junior developers and conducted technical interviews'
    ],
    technologies: ['Vue.js', 'Node.js', 'MongoDB', 'AWS', 'Docker', 'Redis']
  },
  {
    id: 2,
    position: 'Full Stack Developer',
    company: 'Digital Innovations Ltd',
    location: 'Phnom Penh, Cambodia',
    startDate: 'Mar 2020',
    endDate: 'Dec 2021',
    description: 'Developed and maintained multiple client projects including e-commerce platforms, CMS systems, and mobile applications. Collaborated with design and marketing teams.',
    achievements: [
      'Built 15+ successful client projects on time and within budget',
      'Integrated payment gateways for 5 different e-commerce platforms',
      'Reduced bug reports by 50% through comprehensive testing',
      'Improved team productivity by introducing code review process'
    ],
    technologies: ['React', 'Laravel', 'MySQL', 'Vue.js', 'Express.js']
  },
  {
    id: 3,
    position: 'Frontend Developer',
    company: 'StartupXYZ',
    location: 'Remote',
    startDate: 'Jun 2019',
    endDate: 'Feb 2020',
    description: 'Focused on creating responsive and interactive user interfaces for a fintech startup. Worked closely with UX designers to implement pixel-perfect designs.',
    achievements: [
      'Developed responsive design system used across 10+ products',
      'Improved mobile user experience leading to 30% increase in engagement',
      'Implemented accessibility features following WCAG guidelines',
      'Collaborated with international team across 3 time zones'
    ],
    technologies: ['React', 'TypeScript', 'Sass', 'Webpack', 'Jest']
  },
  {
    id: 4,
    position: 'Junior Web Developer',
    company: 'Creative Agency',
    location: 'Phnom Penh, Cambodia',
    startDate: 'Aug 2018',
    endDate: 'May 2019',
    description: 'Started career developing WordPress websites and simple web applications. Gained foundational experience in web development best practices.',
    achievements: [
      'Completed 20+ WordPress projects for small businesses',
      'Learned modern JavaScript frameworks and development tools',
      'Improved page load speeds by optimizing images and code',
      'Received employee of the month award twice'
    ],
    technologies: ['WordPress', 'PHP', 'JavaScript', 'CSS', 'jQuery']
  }
]
</script> 